/**
 * Vanilla melee / ranged combat math (Java 26.x, minecraft.wiki: Damage,
 * Attack cooldown, Mace, Bow). Pure functions; main.ts wires them to input.
 */

import { itemDef } from './itemdb';
import { I } from './itemIds';
import { isEmpty, type Stack } from './types';
import { enchantDamageBonus, sweepDamage } from './enchant/effects';
import { E } from './enchant/registry';
import { enchLevel } from './enchant/stack';

/**
 * Which edition's melee rules apply. Bedrock: no attack cooldown / charge scaling, no sweep
 * attack (Sweeping Edge does not exist), full damage every swing limited only by the target's
 * 10-tick hurt invulnerability, crits (x1.5) allowed while sprinting, Sharpness +1.25/level.
 * Java: cooldown-scaled damage, sweep, sprint-reset crits. Functions below take an optional
 * `edition` so smoke tests can assert both paths.
 */
export type CombatEdition = 'bedrock' | 'java';
export const COMBAT_EDITION: CombatEdition = 'bedrock';

export const FIST_DAMAGE = 1;
export const FIST_SPEED = 4;
/** Crit / sweep / sprint-knockback threshold on the cooldown scale (vanilla > 0.9 of charge → 84.8% of the bar). */
export const FULL_CHARGE = 0.9;

/**
 * Bedrock attack damage by [tier 0 wood/gold .. 4 netherite] (copper uses stone's tier 1):
 * sword 5/6/7/8/9, axe 4/5/6/7/8 (sword - 1), pickaxe 3/4/5/6/7 (sword - 2), shovel 2/3/4/5/6 (sword - 3), hoe 1.
 * Mace 6 and trident 9 match Java.
 */
const BEDROCK_TIER_ATTACK: Partial<Record<string, readonly number[]>> = {
  sword: [5, 6, 7, 8, 9], axe: [4, 5, 6, 7, 8], pickaxe: [3, 4, 5, 6, 7], shovel: [2, 3, 4, 5, 6],
};
export function bedrockToolDamage(type: string, tier: number, javaAttack: number): number {
  const row = BEDROCK_TIER_ATTACK[type];
  return row ? row[Math.max(0, Math.min(4, tier))] : javaAttack;
}

export function weaponAttack(held: Stack | null, edition: CombatEdition = COMBAT_EDITION): { damage: number; speed: number } {
  const t = !held || isEmpty(held) ? undefined : itemDef(held.id).tool;
  if (!t) return { damage: FIST_DAMAGE, speed: FIST_SPEED };
  return { damage: edition === 'bedrock' ? bedrockToolDamage(t.type, t.tier, t.attack) : t.attack, speed: t.attackSpeed };
}

/** Bedrock mobs hit harder with difficulty: easy x0.5 + 1 (cap), normal x1, hard x1.5 (peaceful never hits). */
export function difficultyDamage(damage: number, difficulty: 'peaceful' | 'easy' | 'normal' | 'hard'): number {
  if (difficulty === 'peaceful') return 0;
  if (difficulty === 'easy') return Math.min(damage / 2 + 1, damage);
  return difficulty === 'hard' ? damage * 1.5 : damage;
}

/** Cooldown scale 0..1: (ticksSinceSwing + 0.5) / (20 / attackSpeed). */
export function chargeScale(ticksSinceSwing: number, attackSpeed: number): number {
  return Math.max(0, Math.min(1, (ticksSinceSwing + 0.5) / (20 / attackSpeed)));
}

/** Mace smash bonus for a fall of `fall` blocks (needs > 1.5): 4/block first 3, 2/block next 5, 1/block after; + Density 0.5/block/level. */
export function maceBonus(fall: number, density = 0): number {
  if (fall <= 1.5) return 0;
  const a = Math.min(fall, 3), b = Math.min(Math.max(fall - 3, 0), 5), c = Math.max(fall - 8, 0);
  return 4 * a + 2 * b + c + 0.5 * density * fall;
}

export interface MeleeInput {
  held: Stack | null;
  /** Cooldown scale 0..1. */
  charge: number;
  /** Target mob type (Smite / Bane). */
  targetType: string;
  falling: boolean;
  onGround: boolean;
  inWater: boolean;
  /** Target is in water or rain (Bedrock Impaling). */
  targetWet?: boolean;
  sprinting: boolean;
  /** Blocks fallen so far (mace). */
  fallDistance: number;
}

export interface MeleeResult {
  damage: number;
  crit: boolean;
  /** Knockback velocity strength (0.4 base + 0.5 per level of sprint / Knockback). */
  knockback: number;
  /** Extra knockback levels (sprint hit + Knockback enchant): 0.5 push and 0.1 lift each. */
  kbLevels: number;
  sweep: boolean;
  sweepDamage: number;
  /** Mace smash happened (cancels fall damage, triggers smash knockback / Wind Burst). */
  smash: boolean;
  /** Fraction of armor ignored (Breach 15%/level). */
  armorIgnore: number;
  fireTicks: number;
}

/**
 * Player.attack: base * (0.2 + c^2 * 0.8), crit x1.5 (before enchantments),
 * then enchantment bonus * c. Sprint hits add a knockback level and never crit or sweep.
 */
export function meleeHit(m: MeleeInput, edition: CombatEdition = COMBAT_EDITION): MeleeResult {
  const bedrock = edition === 'bedrock';
  const { damage: base } = weaponAttack(m.held, edition);
  const c = bedrock ? 1 : m.charge;
  const strong = c > FULL_CHARGE;
  let dmg = base * (0.2 + c * c * 0.8);
  const sprintHit = m.sprinting && strong;
  // Bedrock crit: falling, off the ground, not in water and not climbing; sprinting does NOT block it (Java it does)
  const crit = strong && m.falling && !m.onGround && !m.inWater && (bedrock || !m.sprinting);
  if (crit) dmg *= 1.5;
  const isMace = !!m.held && m.held.id === I.MACE;
  let smash = false;
  if (isMace && m.fallDistance > 1.5 && !m.onGround) {
    smash = true;
    dmg += maceBonus(m.fallDistance, enchLevel(m.held, E.DENSITY));
  }
  dmg += enchantDamageBonus(m.held, m.targetType, edition) * c;
  if (bedrock && m.targetWet) dmg += 2.5 * enchLevel(m.held, E.IMPALING);
  const kbLevels = (sprintHit ? 1 : 0) + enchLevel(m.held, E.KNOCKBACK);
  const tool = m.held && !isEmpty(m.held) ? itemDef(m.held.id).tool : undefined;
  // vanilla: full charge, on the ground, not sprinting, not a crit, sword in hand
  const sweep = !bedrock && strong && !m.sprinting && !crit && m.onGround && tool?.type === 'sword';
  return {
    damage: dmg, crit, knockback: 0.4 + 0.5 * kbLevels, kbLevels, sweep,
    sweepDamage: sweep ? sweepDamage(base, enchLevel(m.held, E.SWEEPING_EDGE)) : 0,
    smash, armorIgnore: 0.15 * enchLevel(m.held, E.BREACH),
    fireTicks: enchLevel(m.held, E.FIRE_ASPECT) * 80,
  };
}

/** Armor reduction (CombatRules.getDamageAfterAbsorb). */
export function armorReduce(damage: number, armor: number, toughness: number): number {
  const f = Math.min(20, Math.max(armor / 5, armor - damage / (2 + toughness / 4)));
  return damage * (1 - f / 25);
}

/** Bow: draw ticks → power 0..1 ((t^2 + 2t)/3, t = ticks/20). */
export function bowPower(ticks: number): number {
  const t = ticks / 20;
  return Math.min(1, (t * t + t * 2) / 3);
}

/** Wind Burst launch velocity per level (wiki knockback multipliers 1.2 / 1.75 / 2.2, applied to a ~0.7 b/t base). */
export const windBurstVelocity = (lvl: number): number => (lvl <= 0 ? 0 : [0, 1.2, 1.75, 2.2][Math.min(3, lvl)] * 0.7);
